import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import type { components } from '../../../api/generated/schema'
import {
  Button,
  Card,
  IconButton,
  Input,
  OrderButtons,
  SectionLabel,
} from '../../../design/components'
import { moved } from '../../../lib/order'
import { TIME_OF_DAY_ORDER } from '../../scenes/types'
import styles from '../../scenes/sections/play.module.css'
import { useCreateScene } from '../queries'
import type { CaseScenesSection } from '../types'
import type { CaseEditing } from '../useCaseEditing'

type Schema = components['schemas']

/**
 * The case's scenes, in play order.
 *
 * A row shows only what the case sheet carries — title, when and where, and the counts.
 * The scene's own contents are a separate read, which is the whole point of the case ↔
 * scene cut, so the row is a link rather than an expander.
 *
 * Reordering sends the complete list of ids. The API rejects a partial ordering with a
 * 400 rather than half-applying it, so `moved` returns the whole array and this sends
 * the whole array.
 */
export function CaseScenesCard({
  caseId,
  scenes,
  editing,
}: {
  caseId: number
  scenes: CaseScenesSection | undefined
  editing: CaseEditing
}) {
  const [adding, setAdding] = useState(false)

  if (scenes === undefined) return null

  const all = scenes.scenes ?? []

  return (
    <Card
      variant="magic"
      label="Scenes"
      hint={all.length === 1 ? '1 scene' : `${all.length} scenes`}
      action={
        editing.canEdit && !adding ? (
          <IconButton label="Add a scene" icon="add" onClick={() => setAdding(true)} />
        ) : undefined
      }
    >
      {all.length === 0 && !adding && <p className={styles.empty}>No scenes in this case yet.</p>}

      {all.map((scene, index) => (
        <div key={scene.id} className={styles.row}>
          <div className={styles.rowHead}>
            <Link to={`/scenes/${scene.id}`} className={styles.sceneLink}>
              <span className={styles.sceneTitle}>{scene.title}</span>
              <span className={styles.rowMeta}>
                {[
                  String(scene.timeOfDay),
                  scene.locationName,
                  `${scene.characterCount} in the cast`,
                  scene.aspectCount === 0
                    ? null
                    : `${scene.aspectCount} ${scene.aspectCount === 1 ? 'aspect' : 'aspects'}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </Link>

            {editing.canEdit && (
              <span className={styles.orderButtons}>
                <OrderButtons
                  label={scene.title}
                  index={index}
                  total={all.length}
                  disabled={editing.reorderScenes.isPending}
                  onMove={(to) =>
                    editing.reorderScenes.mutate({
                      ids: moved(
                        all.map((entry) => entry.id),
                        index,
                        to,
                      ),
                    })
                  }
                />
                <Button
                  variant="danger"
                  disabled={editing.deleteScene.isPending}
                  onClick={() => editing.deleteScene.mutate({ sceneId: scene.id })}
                >
                  Delete
                </Button>
              </span>
            )}
          </div>

          {scene.summary !== '' && <p className={styles.rowText}>{scene.summary}</p>}
        </div>
      ))}

      {editing.canEdit && adding && (
        <NewSceneForm caseId={caseId} onClose={() => setAdding(false)} />
      )}
    </Card>
  )
}

/**
 * New scenes are appended last and moved with the order buttons — the API says so, and
 * offering a position here would be a second way to do the same thing.
 *
 * Creating one answers with the scene's own sheet rather than a case envelope, so this
 * uses `useCreateScene` (which invalidates the case) and then goes straight to the
 * tracker — which is what the endpoint was shaped for.
 */
function NewSceneForm({ caseId, onClose }: { caseId: number; onClose: () => void }) {
  const navigate = useNavigate()
  const create = useCreateScene(caseId)

  const [title, setTitle] = useState('')
  const [timeOfDay, setTimeOfDay] = useState<Schema['TimeOfDay']>('Night')
  const [summary, setSummary] = useState('')

  const pending = create.isPending

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        create.mutate(
          {
            title,
            timeOfDay,
            locationId: null,
            summary: summary === '' ? null : summary,
          },
          {
            onSuccess: (sheet) => {
              onClose()
              void navigate(`/scenes/${sheet.sceneId}`)
            },
          },
        )
      }}
    >
      <SectionLabel tone="ember" micro>
        New scene
      </SectionLabel>

      <Input label="Title" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} />

      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          Time of day
        </SectionLabel>
        <select
          className={styles.select}
          value={timeOfDay}
          onChange={(e) => setTimeOfDay(e.target.value as Schema['TimeOfDay'])}
        >
          {TIME_OF_DAY_ORDER.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>

      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          Summary
        </SectionLabel>
        <textarea
          className={styles.textarea}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
        />
      </label>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={pending || title.trim() === ''}>
          {pending ? 'Adding…' : 'Add scene'}
        </Button>
      </div>
    </form>
  )
}
